import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  LuArrowLeft, LuUsers, LuPlus, LuPhone, LuCircleCheck, LuCircleX,
  LuTrash2, LuBan, LuSearch, LuUser, LuUserX,
  LuMail, LuGraduationCap, LuTag, LuPencil, LuSave, LuX, LuSparkles,
  LuMessageSquare, LuBot, LuZap, LuZapOff, LuSend, LuClock,
  LuPaperclip, LuFileText, LuImage, LuCheck, LuCheckCheck, LuLoader
} from "react-icons/lu";
import { API } from "../App";
import { format, formatDistanceToNowStrict } from "date-fns";
import { es } from "date-fns/locale";

const SOURCE_LABELS = {
  inbound: "Mensaje entrante",
  manual: "Manual",
  web_form: "Formulario web",
  template_sent: "Plantilla enviada"
};

const EDUCATION_OPTIONS = ["Bachillerato", "FP", "Grado", "Máster", "Doctorado", "Otro"];

const MsgStatusIcon = ({ status }) => {
  if (!status) return null;
  if (status === "sending") return <LuLoader className="w-3 h-3 text-primary-foreground/50 animate-spin" />;
  if (status === "sent") return <LuCheck className="w-3 h-3 text-primary-foreground/60" title="Enviado" />;
  if (status === "delivered") return <LuCheckCheck className="w-3 h-3 text-primary-foreground/70" title="Entregado" />;
  if (status === "read") return <LuCheckCheck className="w-3 h-3 text-blue-300" title="Leído" />;
  if (status === "failed") return <LuX className="w-3 h-3 text-red-400" title="Fallido" />;
  return null;
};

const LeadsPage = ({ auth }) => {
  const [optins, setOptins] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [onlyReplied, setOnlyReplied] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const [addOptinOpen, setAddOptinOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [optinForm, setOptinForm] = useState({ phone_number: "", source: "manual", notes: "" });

  const [panelTab, setPanelTab] = useState("ficha");
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [sendingMedia, setSendingMedia] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const phoneParam = searchParams.get("phone");
    if (phoneParam) {
      handleSelectLead(phoneParam);
      setPanelTab("conversacion");
    }
  }, []);

  useEffect(() => {
    if (!selectedPhone || panelTab !== "conversacion") return;
    fetchConversations(selectedPhone);
    const interval = setInterval(() => fetchConversations(selectedPhone, true), 5000);
    return () => clearInterval(interval);
  }, [selectedPhone, panelTab]);

  const fetchConversations = async (phone, silent = false) => {
    if (!silent) setConversationsLoading(true);
    try {
      const res = await fetch(`${API}/leads/${phone}/conversations`, { headers: { Authorization: `Bearer ${auth.token}` } });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        setSelectedConvId(prev => (prev && data.some(c => c.id === prev)) ? prev : (data[0]?.id || null));
      }
    } catch {
      if (!silent) toast.error("Error al cargar la conversación");
    } finally {
      if (!silent) setConversationsLoading(false);
    }
  };

  const selectedConv = conversations.find(c => c.id === selectedConvId) || null;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedConvId, selectedConv?.messages?.length]);

  const handleToggleHandover = async () => {
    if (!selectedConv) return;
    const newState = !selectedConv.handover_active;
    try {
      const res = await fetch(`${API}/conversations/${selectedConv.id}/handover`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: newState })
      });
      if (res.ok) {
        toast.success(newState ? "Intervención humana activada" : "IA reactivada");
        fetchConversations(selectedPhone, true);
      }
    } catch {
      toast.error("Error al cambiar modo");
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConv) return;
    try {
      const res = await fetch(`${API}/conversations/${selectedConv.id}/respond`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message: messageText, agent_id: selectedConv.agent_id })
      });
      if (res.ok) {
        setMessageText("");
        fetchConversations(selectedPhone, true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al enviar mensaje");
        fetchConversations(selectedPhone, true);
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const handleSendMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConv) return;
    setSendingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/conversations/${selectedConv.id}/send-media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}` },
        body: formData,
      });
      if (res.ok) {
        toast.success(`Archivo enviado: ${file.name}`);
        fetchConversations(selectedPhone, true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al enviar el archivo");
      }
    } catch {
      toast.error("Error de conexión al enviar el archivo");
    } finally {
      setSendingMedia(false);
      e.target.value = "";
    }
  };

  const formatMsgDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d MMM, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return null;
    try {
      return formatDistanceToNowStrict(new Date(dateStr), { locale: es, addSuffix: true });
    } catch {
      return null;
    }
  };

  const fetchData = async () => {
    try {
      const [optinsRes, profilesRes, agentsRes] = await Promise.all([
        fetch(`${API}/leads/optins`, { headers: { Authorization: `Bearer ${auth.token}` } }),
        fetch(`${API}/leads/profiles`, { headers: { Authorization: `Bearer ${auth.token}` } }),
        fetch(`${API}/agents`, { headers: { Authorization: `Bearer ${auth.token}` } })
      ]);
      if (optinsRes.ok) setOptins(await optinsRes.json());
      if (profilesRes.ok) {
        const profs = await profilesRes.json();
        const map = {};
        profs.forEach(p => { map[p.phone_number] = p; });
        setProfiles(map);
      }
      if (agentsRes.ok) setAgents(await agentsRes.json());
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const getProfile = (phone) => profiles[phone] || null;

  const getDisplayName = (phone) => {
    const p = getProfile(phone);
    return p?.name || null;
  };

  const handleSelectLead = (phone) => {
    setSelectedPhone(phone);
    setEditingProfile(false);
    setPanelTab("ficha");
    setConversations([]);
    setSelectedConvId(null);
    const p = getProfile(phone);
    setProfileForm(p ? { ...p } : { phone_number: phone, name: "", email: "", education_level: "", interests: [], observations: "", tags: [] });
  };

  const handleSaveProfile = async () => {
    if (!selectedPhone) return;
    setProfileSaving(true);
    try {
      const res = await fetch(`${API}/leads/${selectedPhone}/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify(profileForm)
      });
      if (res.ok) {
        const updated = await res.json();
        setProfiles(prev => ({ ...prev, [updated.phone_number]: updated }));
        setProfileForm(updated);
        setEditingProfile(false);
        toast.success("Ficha guardada");
      } else {
        toast.error("Error al guardar ficha");
      }
    } catch {
      toast.error("Error de conexión");
    }
    setProfileSaving(false);
  };

  const handleAddTag = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (!profileForm.tags?.includes(tag)) {
        setProfileForm(f => ({ ...f, tags: [...(f.tags || []), tag] }));
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setProfileForm(f => ({ ...f, tags: (f.tags || []).filter(t => t !== tag) }));
  };

  const handleOptout = async (phone) => {
    if (!window.confirm(`¿Registrar opt-out para ${phone}? El agente dejará de responderle.`)) return;
    const res = await fetch(`${API}/leads/${phone}/optout`, { method: "POST", headers: { Authorization: `Bearer ${auth.token}` } });
    if (res.ok) { toast.success("Opt-out registrado"); fetchData(); }
    else toast.error("Error al registrar opt-out");
  };

  const handleReOptin = async (phone) => {
    const res = await fetch(`${API}/leads/optin`, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ phone_number: phone, source: "manual", notes: "Reactivado manualmente" })
    });
    if (res.ok) { toast.success("Opt-in reactivado"); fetchData(); }
    else toast.error("Error al reactivar");
  };

  const handleBlock = async (phone) => {
    if (!window.confirm(`¿Bloquear ${phone}? El agente nunca le responderá.`)) return;
    const res = await fetch(`${API}/leads/${phone}/block`, { method: "POST", headers: { Authorization: `Bearer ${auth.token}` } });
    if (res.ok) { toast.success("Contacto bloqueado"); fetchData(); }
    else toast.error("Error al bloquear");
  };

  const handleUnblock = async (phone) => {
    const res = await fetch(`${API}/leads/${phone}/unblock`, { method: "POST", headers: { Authorization: `Bearer ${auth.token}` } });
    if (res.ok) { toast.success("Contacto desbloqueado"); fetchData(); }
    else toast.error("Error al desbloquear");
  };

  const handleDeleteLead = async (leadId, phone) => {
    if (!window.confirm(`¿Eliminar el lead ${phone} permanentemente? Se borrarán sus datos y ficha.`)) return;
    const res = await fetch(`${API}/leads/${leadId}`, { method: "DELETE", headers: { Authorization: `Bearer ${auth.token}` } });
    if (res.ok) {
      toast.success("Lead eliminado");
      setSelectedPhone(null);
      fetchData();
    } else toast.error("Error al eliminar el lead");
  };

  const handleAddOptin = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await fetch(`${API}/leads/optin`, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
      body: JSON.stringify(optinForm)
    });
    if (res.ok) {
      toast.success("Contacto añadido");
      setAddOptinOpen(false);
      setOptinForm({ phone_number: "", source: "manual", notes: "" });
      fetchData();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Error al añadir contacto");
    }
    setSaving(false);
  };

  const activeCount = optins.filter(o => o.status === "active").length;
  const optedOutCount = optins.filter(o => o.status === "opted_out").length;
  const blockedCount = optins.filter(o => o.status === "blocked").length;
  const repliedCount = optins.filter(o => o.has_customer_reply).length;

  const STATUS_FILTERS = [
    { key: "all", label: "Todos", count: optins.length, color: "text-foreground", activeBg: "bg-primary/15 border-primary/30 text-foreground" },
    { key: "active", label: "Activos", count: activeCount, color: "text-green-400", activeBg: "bg-green-500/15 border-green-500/30 text-green-400" },
    { key: "opted_out", label: "Opt-out", count: optedOutCount, color: "text-red-400", activeBg: "bg-red-500/15 border-red-500/30 text-red-400" },
    { key: "blocked", label: "Bloqueados", count: blockedCount, color: "text-orange-400", activeBg: "bg-orange-500/15 border-orange-500/30 text-orange-400" },
  ];

  const filteredOptins = optins
    .filter(o => statusFilter === "all" || o.status === statusFilter)
    .filter(o => agentFilter === "all" || (o.agent_ids || []).includes(agentFilter))
    .filter(o => !onlyReplied || o.has_customer_reply)
    .filter(o => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const digits = search.replace(/\D/g, "");
      const name = getDisplayName(o.phone_number)?.toLowerCase() || "";
      return o.phone_number.includes(digits) || name.includes(q) || (o.notes || "").toLowerCase().includes(q);
    });

  const selectedOptin = selectedPhone ? optins.find(o => o.phone_number === selectedPhone) : null;
  const selectedProfile = selectedPhone ? getProfile(selectedPhone) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="rounded-xl">
              <LuArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <LuUsers className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-foreground">Gestión de Leads</h1>
              <p className="text-xs text-muted-foreground">Fichas de clientes y opt-ins</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 sm:py-5 flex flex-col gap-4 h-[calc(100vh-73px)]">

        {/* Barra de filtros + aviso de cumplimiento, compacta */}
        <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                  statusFilter === f.key ? f.activeBg : "bg-muted/30 border-white/10 text-muted-foreground hover:bg-muted/50"
                }`}
              >
                {f.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${statusFilter === f.key ? "bg-black/10" : "bg-white/10"}`}>{f.count}</span>
              </button>
            ))}
            {agents.length > 1 && (
              <>
                <span className="w-px h-5 bg-white/10 mx-1" />
                <select
                  value={agentFilter}
                  onChange={e => setAgentFilter(e.target.value)}
                  className="bg-muted/30 border border-white/10 rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-all outline-none"
                  title="Filtrar por agente que ha interactuado con el lead"
                >
                  <option value="all">Todos los agentes</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </>
            )}
            <span className="w-px h-5 bg-white/10 mx-1" />
            <button
              onClick={() => setOnlyReplied(v => !v)}
              title="Mostrar solo leads donde el cliente ha respondido al menos una vez"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                onlyReplied ? "bg-primary/15 border-primary/30 text-foreground" : "bg-muted/30 border-white/10 text-muted-foreground hover:bg-muted/50"
              }`}
            >
              <LuMessageSquare className="w-3.5 h-3.5" />
              Ha respondido
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${onlyReplied ? "bg-black/10" : "bg-white/10"}`}>{repliedCount}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">

              {/* Lista de contactos */}
              <div className={`${selectedPhone ? "hidden lg:flex" : "flex"} flex-1 min-h-0 lg:flex-none lg:w-[32%] flex-col gap-3`}>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input type="text" placeholder="Buscar por nombre, teléfono..."
                      className="w-full bg-muted/50 border border-transparent rounded-xl pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                      value={search} onChange={e => setSearch(e.target.value)} />
                  </div>
                  <div className="flex gap-1">
                    <Dialog open={addOptinOpen} onOpenChange={setAddOptinOpen}>
                      <DialogTrigger asChild>
                        <Button size="icon" className="rounded-xl bg-primary hover:bg-primary/90 shrink-0" title="Añadir contacto">
                          <LuPlus className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card border-border max-w-md">
                        <DialogHeader><DialogTitle className="font-heading text-foreground">Registrar opt-in manual</DialogTitle></DialogHeader>
                        <form onSubmit={handleAddOptin} className="space-y-4 mt-4">
                          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-muted-foreground">
                            Al registrar confirmas que el contacto ha dado su consentimiento explícito.
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">Teléfono</Label>
                            <Input placeholder="34612345678" className="bg-muted/50 border-transparent rounded-xl font-mono"
                              value={optinForm.phone_number} onChange={e => setOptinForm({ ...optinForm, phone_number: e.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">Origen</Label>
                            <select className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground"
                              value={optinForm.source} onChange={e => setOptinForm({ ...optinForm, source: e.target.value })}>
                              <option value="manual">Manual</option>
                              <option value="web_form">Formulario web</option>
                              <option value="inbound">Mensaje entrante</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">Notas</Label>
                            <Input placeholder="Ej: Se registró en la landing" className="bg-muted/50 border-transparent rounded-xl"
                              value={optinForm.notes} onChange={e => setOptinForm({ ...optinForm, notes: e.target.value })} />
                          </div>
                          <div className="flex justify-end gap-3 pt-2">
                            <Button type="button" variant="ghost" onClick={() => setAddOptinOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="rounded-full bg-primary" disabled={saving}>{saving ? "Guardando..." : "Registrar"}</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground shrink-0">{filteredOptins.length} de {optins.length} contactos</p>

                <ScrollArea className="flex-1 min-h-0">
                  <div className="space-y-2 pr-2">
                    {filteredOptins.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground text-sm">
                        {search ? "Sin resultados." : "Aún no hay contactos."}
                      </div>
                    ) : filteredOptins.map((o, i) => {
                      const name = getDisplayName(o.phone_number);
                      const isSelected = selectedPhone === o.phone_number;
                      const hoursLeft = o.last_message_at ? Math.max(0, 24 - (Date.now() - new Date(o.last_message_at).getTime()) / 3600000) : null;
                      return (
                        <motion.button key={o.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 20) * 0.02 }}
                          onClick={() => handleSelectLead(o.phone_number)}
                          className={`w-full text-left p-3 rounded-xl border-l-4 border transition-all ${
                            isSelected ? "bg-primary/10 border-primary/30 border-l-primary" : "bg-muted/20 border-white/5 hover:bg-muted/40"
                          } ${!isSelected && o.status === "active" ? "border-l-green-500/60" : ""} ${!isSelected && o.status === "opted_out" ? "border-l-red-500/60" : ""} ${!isSelected && o.status === "blocked" ? "border-l-orange-500/60" : ""}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
                              o.status === "active" ? "bg-green-500/20 text-green-400" : o.status === "blocked" ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400"
                            }`}>
                              {name ? name[0].toUpperCase() : <LuPhone className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-medium text-foreground truncate">{name || `+${o.phone_number}`}</p>
                                {o.last_message_at && (
                                  <span className="text-[10px] text-muted-foreground shrink-0">{formatRelative(o.last_message_at)}</span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono truncate">+{o.phone_number}</p>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {o.last_message_preview
                                  ? <>{o.last_message_role === "user" ? "" : <span className="text-primary/70">Tú: </span>}{o.last_message_preview}</>
                                  : <span className="italic">Sin conversación todavía</span>}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {o.unread && <span className="w-2 h-2 rounded-full bg-emerald-400" title="Sin leer" />}
                              {hoursLeft !== null && hoursLeft === 0 && <LuClock className="w-3 h-3 text-red-400" title="Ventana de 24h cerrada" />}
                              {hoursLeft !== null && hoursLeft > 0 && hoursLeft < 4 && <LuClock className="w-3 h-3 text-amber-400" title="Ventana cerrando" />}
                              {o.status === "blocked" && <LuBan className="w-3 h-3 text-orange-400" />}
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>

              {/* Panel de ficha */}
              <div className={`${!selectedPhone ? "hidden lg:flex" : "flex"} flex-1 min-h-0 flex-col`}>
                <AnimatePresence mode="wait">
                  {!selectedPhone ? (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="glass-card rounded-2xl border border-white/10 p-12 text-center h-full flex flex-col items-center justify-center">
                      <LuUser className="w-12 h-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Selecciona un contacto para ver su ficha</p>
                    </motion.div>
                  ) : (
                    <motion.div key={selectedPhone} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                      className="glass-card rounded-2xl border border-white/10 overflow-hidden h-full flex flex-col">

                      {/* Header ficha */}
                      <div className="p-5 border-b border-white/10 flex items-start gap-4 shrink-0">
                        <Button variant="ghost" size="icon" className="rounded-xl shrink-0 lg:hidden -ml-1" onClick={() => setSelectedPhone(null)}>
                          <LuArrowLeft className="w-5 h-5" />
                        </Button>
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold shrink-0 ${
                          selectedOptin?.status === "active" ? "bg-green-500/20 text-green-400" : selectedOptin?.status === "blocked" ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400"
                        }`}>
                          {selectedProfile?.name ? selectedProfile.name[0].toUpperCase() : <LuUser className="w-6 h-6" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="font-heading font-bold text-lg text-foreground">
                              {selectedProfile?.name || `+${selectedPhone}`}
                            </h2>
                            {selectedOptin?.status === "active" && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs"><LuCircleCheck className="w-3 h-3 mr-1" />Activo</Badge>}
                            {selectedOptin?.status === "opted_out" && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs"><LuCircleX className="w-3 h-3 mr-1" />Opt-out</Badge>}
                            {selectedOptin?.status === "blocked" && <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs"><LuBan className="w-3 h-3 mr-1" />Bloqueado</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground font-mono mt-0.5">+{selectedPhone}</p>
                          {selectedProfile?.phone_variants?.length > 1 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Variantes: {selectedProfile.phone_variants.map(v => `+${v}`).join(", ")}
                            </p>
                          )}
                          {selectedOptin && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {SOURCE_LABELS[selectedOptin.source] || selectedOptin.source} · {format(new Date(selectedOptin.consented_at), "d MMM yyyy", { locale: es })}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {panelTab === "ficha" && (!editingProfile ? (
                            <Button variant="ghost" size="sm" className="rounded-xl text-xs gap-1" onClick={() => setEditingProfile(true)}>
                              <LuPencil className="w-3.5 h-3.5" />Editar
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="sm" className="rounded-xl text-xs gap-1 text-muted-foreground" onClick={() => { setEditingProfile(false); setProfileForm(selectedProfile || {}); }}>
                                <LuX className="w-3.5 h-3.5" />Cancelar
                              </Button>
                              <Button size="sm" className="rounded-xl text-xs gap-1 bg-primary" onClick={handleSaveProfile} disabled={profileSaving}>
                                <LuSave className="w-3.5 h-3.5" />{profileSaving ? "..." : "Guardar"}
                              </Button>
                            </>
                          ))}
                        </div>
                      </div>

                      <Tabs value={panelTab} onValueChange={setPanelTab} className="flex-1 min-h-0 flex flex-col">
                        <TabsList className="mx-5 mt-3 bg-muted/50 p-1 rounded-xl w-fit shrink-0">
                          <TabsTrigger value="ficha" className="rounded-lg data-[state=active]:bg-card text-xs">
                            <LuUser className="w-3.5 h-3.5 mr-1.5" />Ficha
                          </TabsTrigger>
                          <TabsTrigger value="conversacion" className="rounded-lg data-[state=active]:bg-card text-xs">
                            <LuMessageSquare className="w-3.5 h-3.5 mr-1.5" />Conversación
                          </TabsTrigger>
                        </TabsList>

                      {/* TAB: FICHA */}
                      <TabsContent value="ficha" className="mt-0 flex-1 min-h-0">
                      <ScrollArea className="h-full">
                        <div className="p-5 space-y-5">
                          {/* IA badge */}
                          {selectedProfile && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <LuSparkles className="w-3.5 h-3.5 text-primary" />
                              <span>Ficha actualizada automáticamente por IA · {selectedProfile.updated_at ? format(new Date(selectedProfile.updated_at), "d MMM HH:mm", { locale: es }) : "—"}</span>
                            </div>
                          )}

                          <div className="rounded-xl border border-white/10 bg-muted/10 p-4 space-y-4">
                            <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Datos de contacto</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              {/* Nombre */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><LuUser className="w-3 h-3" />Nombre</Label>
                                {editingProfile ? (
                                  <Input value={profileForm.name || ""} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                                    className="bg-muted/50 border-transparent rounded-xl text-sm h-9" placeholder="Nombre completo" />
                                ) : (
                                  <p className="text-sm text-foreground">{selectedProfile?.name || <span className="text-muted-foreground italic">Sin datos</span>}</p>
                                )}
                              </div>
                              {/* Email */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><LuMail className="w-3 h-3" />Email</Label>
                                {editingProfile ? (
                                  <Input value={profileForm.email || ""} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                                    className="bg-muted/50 border-transparent rounded-xl text-sm h-9" placeholder="email@ejemplo.com" />
                                ) : (
                                  <p className="text-sm text-foreground">{selectedProfile?.email || <span className="text-muted-foreground italic">Sin datos</span>}</p>
                                )}
                              </div>
                              {/* Nivel educativo */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><LuGraduationCap className="w-3 h-3" />Nivel educativo</Label>
                                {editingProfile ? (
                                  <select value={profileForm.education_level || ""} onChange={e => setProfileForm(f => ({ ...f, education_level: e.target.value }))}
                                    className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground h-9">
                                    <option value="">Sin datos</option>
                                    {EDUCATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                ) : (
                                  <p className="text-sm text-foreground">{selectedProfile?.education_level || <span className="text-muted-foreground italic">Sin datos</span>}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl border border-white/10 bg-muted/10 p-4 space-y-4">
                            <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Cualificación</p>
                            {/* Intereses */}
                            <div className="space-y-1.5">
                              <Label className="text-xs text-muted-foreground">Intereses detectados</Label>
                              <div className="flex flex-wrap gap-1.5">
                                {(editingProfile ? profileForm.interests : selectedProfile?.interests || []).map(interest => (
                                  <span key={interest} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                                    {interest}
                                    {editingProfile && (
                                      <button onClick={() => setProfileForm(f => ({ ...f, interests: f.interests.filter(i => i !== interest) }))} className="ml-0.5 hover:text-red-400">×</button>
                                    )}
                                  </span>
                                ))}
                                {editingProfile && (
                                  <input placeholder="+ interés (Enter)" value={tagInput} onChange={e => setTagInput(e.target.value)}
                                    onKeyDown={e => {
                                      if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                                        e.preventDefault();
                                        const val = tagInput.trim().toLowerCase();
                                        if (!profileForm.interests?.includes(val)) setProfileForm(f => ({ ...f, interests: [...(f.interests || []), val] }));
                                        setTagInput("");
                                      }
                                    }}
                                    className="text-xs px-2.5 py-1 rounded-full bg-muted/50 border border-dashed border-white/20 text-foreground focus:outline-none focus:border-primary w-36" />
                                )}
                                {!editingProfile && !(selectedProfile?.interests?.length) && <span className="text-xs text-muted-foreground italic">Sin datos</span>}
                              </div>
                            </div>

                            {/* Tags */}
                            <div className="space-y-1.5">
                              <Label className="text-xs text-muted-foreground flex items-center gap-1"><LuTag className="w-3 h-3" />Etiquetas</Label>
                              <div className="flex flex-wrap gap-1.5">
                                {(editingProfile ? profileForm.tags : selectedProfile?.tags || []).map(tag => (
                                  <span key={tag} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-white/10">
                                    {tag}
                                    {editingProfile && (
                                      <button onClick={() => handleRemoveTag(tag)} className="ml-0.5 hover:text-red-400">×</button>
                                    )}
                                  </span>
                                ))}
                                {editingProfile && (
                                  <input placeholder="+ etiqueta (Enter)" value={tagInput.startsWith("#") ? tagInput : ""}
                                    onChange={e => setTagInput(e.target.value)}
                                    onKeyDown={e => {
                                      if ((e.key === "Enter" || e.key === ",") && e.target.value.trim()) {
                                        e.preventDefault();
                                        const val = e.target.value.trim().toLowerCase();
                                        if (!profileForm.tags?.includes(val)) setProfileForm(f => ({ ...f, tags: [...(f.tags || []), val] }));
                                        e.target.value = "";
                                      }
                                    }}
                                    className="text-xs px-2.5 py-1 rounded-full bg-muted/50 border border-dashed border-white/20 text-foreground focus:outline-none focus:border-primary w-36" />
                                )}
                                {!editingProfile && !(selectedProfile?.tags?.length) && <span className="text-xs text-muted-foreground italic">Sin etiquetas</span>}
                              </div>
                            </div>
                          </div>

                          {/* Observaciones */}
                          <div className="rounded-xl border border-white/10 bg-muted/10 p-4 space-y-1.5">
                            <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Observaciones</p>
                            {editingProfile ? (
                              <textarea rows={4} value={profileForm.observations || ""}
                                onChange={e => setProfileForm(f => ({ ...f, observations: e.target.value }))}
                                className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:border-primary" />
                            ) : (
                              <p className="text-sm text-foreground min-h-[40px]">
                                {selectedProfile?.observations || <span className="text-muted-foreground italic">Sin observaciones</span>}
                              </p>
                            )}
                          </div>

                          {/* Acciones sobre el contacto */}
                          <div className="pt-2 border-t border-white/10 flex flex-wrap gap-2">
                            {selectedOptin?.status === "active" && (
                              <>
                                <Button variant="outline" size="sm" className="text-xs rounded-lg border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-400 gap-1"
                                  onClick={() => handleOptout(selectedPhone)}>
                                  <LuUserX className="w-3.5 h-3.5" />Opt-out
                                </Button>
                                <Button variant="ghost" size="sm" className="text-xs rounded-lg text-muted-foreground hover:text-orange-400 gap-1"
                                  onClick={() => handleBlock(selectedPhone)}>
                                  <LuBan className="w-3.5 h-3.5" />Bloquear
                                </Button>
                              </>
                            )}
                            {selectedOptin?.status === "opted_out" && (
                              <>
                                <Button variant="ghost" size="sm" className="text-xs rounded-lg text-muted-foreground hover:text-green-400 gap-1"
                                  onClick={() => handleReOptin(selectedPhone)}>
                                  <LuCircleCheck className="w-3.5 h-3.5" />Reactivar
                                </Button>
                                <Button variant="ghost" size="sm" className="text-xs rounded-lg text-muted-foreground hover:text-orange-400 gap-1"
                                  onClick={() => handleBlock(selectedPhone)}>
                                  <LuBan className="w-3.5 h-3.5" />Bloquear
                                </Button>
                              </>
                            )}
                            {selectedOptin?.status === "blocked" && (
                              <Button variant="ghost" size="sm" className="text-xs rounded-lg text-muted-foreground hover:text-green-400 gap-1"
                                onClick={() => handleUnblock(selectedPhone)}>
                                <LuCircleCheck className="w-3.5 h-3.5" />Desbloquear
                              </Button>
                            )}
                            {selectedOptin && (
                              <Button variant="ghost" size="sm" className="text-xs rounded-lg text-muted-foreground hover:text-destructive gap-1 ml-auto"
                                onClick={() => handleDeleteLead(selectedOptin.id, selectedPhone)}>
                                <LuTrash2 className="w-3.5 h-3.5" />Eliminar lead
                              </Button>
                            )}
                          </div>
                        </div>
                      </ScrollArea>
                      </TabsContent>

                      {/* TAB: CONVERSACIÓN */}
                      <TabsContent value="conversacion" className="mt-0 flex-1 min-h-0">
                      <div className="h-full flex flex-col">
                        {conversationsLoading ? (
                          <div className="flex-1 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                          </div>
                        ) : conversations.length === 0 ? (
                          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                            <LuMessageSquare className="w-10 h-10 text-muted-foreground mb-3" />
                            <p className="text-muted-foreground text-sm">Sin conversaciones con este contacto todavía.</p>
                          </div>
                        ) : (
                          <>
                            {conversations.length > 1 && (
                              <div className="px-5 pt-3">
                                <select
                                  className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-1.5 text-xs text-foreground"
                                  value={selectedConvId || ""}
                                  onChange={e => setSelectedConvId(e.target.value)}
                                >
                                  {conversations.map(c => (
                                    <option key={c.id} value={c.id}>
                                      Agente {c.agent_id.slice(0, 8)} · {formatMsgDate(c.updated_at)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div className="px-5 pt-3 flex items-center justify-between gap-2">
                              <p className="text-xs text-muted-foreground">
                                {selectedConv?.last_user_message_at && (() => {
                                  const diff = Date.now() - new Date(selectedConv.last_user_message_at).getTime();
                                  const hoursLeft = Math.max(0, 24 - diff / 3600000);
                                  if (hoursLeft === 0) return (
                                    <span className="inline-flex items-center gap-1 text-red-400"><LuClock className="w-3 h-3" />Ventana de 24h cerrada</span>
                                  );
                                  return <span className="inline-flex items-center gap-1"><LuClock className="w-3 h-3" />{hoursLeft.toFixed(0)}h restantes de ventana</span>;
                                })()}
                              </p>
                              <Button
                                variant={selectedConv?.handover_active ? "secondary" : "outline"}
                                size="sm"
                                onClick={handleToggleHandover}
                                className={`rounded-xl text-xs gap-1.5 ${selectedConv?.handover_active ? "bg-amber-500/20 text-amber-500 hover:bg-amber-500/30" : ""}`}
                              >
                                {selectedConv?.handover_active ? (<><LuZap className="w-3.5 h-3.5" />Interviniendo</>) : (<><LuZapOff className="w-3.5 h-3.5" />Modo IA</>)}
                              </Button>
                            </div>
                            <ScrollArea className="flex-1 min-h-0 px-5">
                              <div className="space-y-3 py-3">
                                {selectedConv?.messages?.map((msg, index) => (
                                  <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                    <div className={`flex items-end gap-2 max-w-[85%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${msg.role === "user" ? "bg-primary/10" : "bg-secondary/20"}`}>
                                        {msg.role === "user" ? (
                                          <LuUser className="w-3.5 h-3.5 text-primary" />
                                        ) : (msg.content || "").startsWith("(Humano):") ? (
                                          <LuUsers className="w-3.5 h-3.5 text-amber-500" />
                                        ) : (
                                          <LuBot className="w-3.5 h-3.5 text-secondary-foreground" />
                                        )}
                                      </div>
                                      <div className={`rounded-2xl px-3.5 py-2.5 ${msg.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md"}`}>
                                        {msg.media_type ? (
                                          <div className="flex items-center gap-2">
                                            {msg.media_type === "image" ? <LuImage className="w-4 h-4 shrink-0" /> : <LuFileText className="w-4 h-4 shrink-0" />}
                                            <span className="text-sm">{msg.filename || (msg.content || "").replace("(Humano):", "").replace("📎", "").trim()}</span>
                                          </div>
                                        ) : (
                                          <p className="text-sm whitespace-pre-wrap">
                                            {(msg.content || "").startsWith("(Humano):") ? msg.content.replace("(Humano):", "").trim() : msg.content}
                                          </p>
                                        )}
                                        <p className={`text-[10px] mt-1 flex items-center gap-1 ${msg.role === "user" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                                          {formatMsgDate(msg.timestamp)}
                                          {msg.role !== "user" && <MsgStatusIcon status={msg.msg_status} />}
                                        </p>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                                <div ref={messagesEndRef} />
                              </div>
                            </ScrollArea>
                            <div className="p-4 border-t border-white/10 bg-muted/20">
                              <div className="flex gap-2">
                                {selectedConv?.handover_active && (
                                  <>
                                    <input ref={fileInputRef} type="file" className="hidden"
                                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,audio/*,video/*"
                                      onChange={handleSendMedia} />
                                    <Button variant="outline" size="icon" className="rounded-xl w-10 h-10 shrink-0"
                                      onClick={() => fileInputRef.current?.click()} disabled={sendingMedia} title="Adjuntar archivo">
                                      {sendingMedia ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> : <LuPaperclip className="w-4 h-4" />}
                                    </Button>
                                  </>
                                )}
                                <input
                                  type="text"
                                  placeholder={selectedConv?.handover_active ? "Escribe como humano..." : "Activa 'Interviniendo' para responder manualmente"}
                                  className="flex-1 min-w-0 h-10 bg-background border border-white/10 rounded-xl px-3 text-sm outline-none focus:border-primary transition-all"
                                  value={messageText}
                                  onChange={e => setMessageText(e.target.value)}
                                  onKeyDown={e => e.key === "Enter" && handleSendMessage()}
                                  disabled={!selectedConv?.handover_active}
                                />
                                <Button onClick={handleSendMessage} size="icon" className="rounded-xl w-10 h-10 shrink-0"
                                  disabled={!messageText.trim() || !selectedConv?.handover_active}>
                                  <LuSend className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      </TabsContent>
                      </Tabs>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
      </main>
    </div>
  );
};

export default LeadsPage;
